import { reactive, ref } from 'vue'
import type { PaginationMeta } from '@/types/common'

type FilterValue = string | string[] | number | boolean

export function useListFilters<T extends Record<string, FilterValue> & { page: number }>(
  defaults: T,
) {
  const initialValues = { ...defaults }
  const filters = reactive({ ...defaults }) as T
  const meta = ref<PaginationMeta | null>(null)

  function setFilters(newFilters: Partial<Omit<T, 'page'>>) {
    Object.assign(filters, newFilters)
    filters.page = 1
  }

  function setPage(page: number) {
    filters.page = page
  }

  function resetFilters() {
    const fresh = { ...initialValues }
    // Deep-copy arrays so reset gives new instances
    for (const key of Object.keys(fresh)) {
      const val = fresh[key as keyof typeof fresh]
      if (Array.isArray(val)) {
        const rec = fresh as Record<string, FilterValue>
        rec[key] = [...val]
      }
    }
    Object.assign(filters, fresh)
  }

  function buildParams(): Record<string, unknown> {
    const params: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(filters)) {
      if (Array.isArray(value)) {
        if (value.length > 0) params[key] = value
      } else if (typeof value === 'boolean') {
        if (value) params[key] = value
      } else if (typeof value === 'string') {
        if (value !== '') params[key] = value
      } else {
        // numbers (page, etc.) are always included
        params[key] = value
      }
    }
    return params
  }

  return { filters, meta, setFilters, setPage, resetFilters, buildParams }
}
